import React, { memo } from 'react';
import { styled } from '@compiled/react';
import memoizeOne from 'memoize-one';
import Icon from '@atlaskit/icon';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import type { StatusCategoryKeys } from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch';
import messages from './messages';
import { getStatusAppearance } from './utils';

export type Props = {
	isUserAtLeastMember: boolean;
	value: {
		id: string;
		name: string;
		category: StatusCategoryKeys;
	};
};

export const StatusFieldStatic = ({ isUserAtLeastMember, value: { name, category } }: Props) => {
	const { formatMessage } = useIntl();

	const ChevronDownGlyph = memoizeOne(() => (
		<svg width={12} height={12} viewBox="5 5 13 13">
			<path
				d="M8.292 10.293a1.009 1.009 0 0 0 0 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 0 0 0-1.419.987.987 0 0 0-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 0 0-1.406 0z"
				fill="currentColor"
				fillRule="evenodd"
			/>
		</svg>
	));

	return (
		<StaticLozengeWrapper>
			<ScreenReaderText>{formatMessage(messages.statusLozenge)}</ScreenReaderText>
			<Lozenge
				appearance={getStatusAppearance(category)}
				{...(isVisualRefreshEnabled() ? { isBold: true } : {})}
			>
				{isUserAtLeastMember ? (
					<StaticLozengeContentWrapper>
						{name}
						<Icon glyph={ChevronDownGlyph} label="" />
					</StaticLozengeContentWrapper>
				) : (
					name
				)}
			</Lozenge>
		</StaticLozengeWrapper>
	);
};

export default memo<Props>(StatusFieldStatic);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StaticLozengeContentWrapper = styled.div({
	width: '100%',
	display: 'inline-block',

	verticalAlign: 'top', // not perfectly vertically centered without this
	alignItems: 'center',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StaticLozengeWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span': {
		// PX values as these are to match the focus outline offset (implemented as a 2px border)
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Ignored via go/DSP-18766
		margin: '2px',

		padding: `0 ${token('space.050', '4px')}`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > span > span': {
		font: token(
			'font.body.small',
			'normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),

		fontWeight: 700,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& div': {
		margin: 0,
	},
});
